'use client'

import { useCallback } from 'react'

import { useUser } from '@auth0/nextjs-auth0'

import { initializeUserRegionalData as _initializeUserRegionalData } from '@common/modules/user'
import { useUserStore } from '@common/modules/user/stores'
import type { InitializeRegionalInput } from '@common/modules/user/types'

export function useUserRegionalData() {
  const { user: auth0User, isLoading: auth0Loading } = useUser()
  const { currentUser, loading, error, setCurrentUser, setState } = useUserStore()

  const initializeUserRegionalData = useCallback(
    async (auth0UserOverride?: InitializeRegionalInput['auth0User'] | null) => {
      const resolvedAuth0User = auth0UserOverride ?? auth0User

      try {
        if (!resolvedAuth0User) return
        if (!auth0UserOverride && auth0Loading) return

        setState({ loading: true, error: null })
        const latestCurrentUser = useUserStore.getState().currentUser

        const { regionalData, regionalIdentity } = await _initializeUserRegionalData({
          auth0User: resolvedAuth0User,
          currentUser: latestCurrentUser,
        })

        const dataChanged =
          JSON.stringify(latestCurrentUser?.regionalData) !== JSON.stringify(regionalData) ||
          JSON.stringify(latestCurrentUser?.regionalIdentity) !== JSON.stringify(regionalIdentity)

        if (dataChanged) {
          setCurrentUser({
            ...(latestCurrentUser ?? {}),
            regionalData: regionalData ?? undefined,
            regionalIdentity: regionalIdentity ?? undefined,
          })
        }
      } finally {
        setState({ loading: false })
      }
    },
    [auth0Loading, auth0User, setCurrentUser, setState]
  )

  return {
    currentIdentity: currentUser?.regionalIdentity ?? null,
    loading,
    error,
    initializeUserRegionalData,
  }
}
